import styled, { css } from 'styled-components';
import { colors, gradients, spacing } from '../../constants/theme';

// Base input styles
const baseInputStyles = css`
  width: 100%;
  border: 1px solid ${colors.background.tertiary};
  border-radius: 8px;
  background: ${colors.background.primary};
  color: ${colors.text.primary};
  font-size: 16px;
  transition: all 0.2s ease;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}20;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &::placeholder {
    color: ${colors.text.secondary};
  }
`;

export const Input = styled.input<{ 
  size?: 'sm' | 'md' | 'lg';
  hasError?: boolean;
}>`
  ${baseInputStyles}
  
  ${props => {
    switch (props.size) {
      case 'sm':
        return css`
          padding: ${spacing.sm} ${spacing.md};
          font-size: 14px;
        `;
      case 'lg':
        return css`
          padding: ${spacing.lg} ${spacing.xl};
          font-size: 18px;
        `;
      default:
        return css`
          padding: ${spacing.md};
        `;
    }
  }}

  ${props => props.hasError && css`
    border-color: ${colors.error};
    
    &:focus {
      border-color: ${colors.error};
      box-shadow: 0 0 0 3px ${colors.error}20;
    }
  `}
`;

export const TextArea = styled.textarea<{ 
  minHeight?: string;
  hasError?: boolean;
}>`
  ${baseInputStyles}
  padding: ${spacing.md};
  resize: vertical;
  min-height: ${props => props.minHeight || '100px'};

  ${props => props.hasError && css`
    border-color: ${colors.error};
    
    &:focus {
      border-color: ${colors.error};
      box-shadow: 0 0 0 3px ${colors.error}20;
    }
  `}
`;

export const Select = styled.select<{ hasError?: boolean }>`
  ${baseInputStyles}
  padding: ${spacing.md};
  cursor: pointer;

  ${props => props.hasError && css`
    border-color: ${colors.error};
    
    &:focus {
      border-color: ${colors.error};
      box-shadow: 0 0 0 3px ${colors.error}20;
    }
  `}
`;

export const FormGroup = styled.div<{ spacing?: 'sm' | 'md' | 'lg' }>`
  margin-bottom: ${props => {
    switch (props.spacing) {
      case 'sm': return spacing.md;
      case 'lg': return spacing.xl;
      default: return spacing.lg;
    }
  }};
`;

export const Label = styled.label<{ required?: boolean }>`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin-bottom: ${spacing.sm};
  
  ${props => props.required && css`
    &::after {
      content: ' *';
      color: ${colors.error};
    }
  `}
`;

export const ErrorText = styled.div`
  color: ${colors.error};
  font-size: 14px;
  margin-top: ${spacing.sm};
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
`;

export const SuccessText = styled.div`
  color: ${colors.success};
  font-size: 14px;
  margin-top: ${spacing.sm};
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
`;

export const HelpText = styled.div`
  color: ${colors.text.hint};
  font-size: 12px;
  margin-top: ${spacing.xs};
`;

export const FieldSet = styled.fieldset`
  border: 1px solid ${colors.background.tertiary};
  border-radius: 8px;
  padding: ${spacing.lg};
  margin: 0 0 ${spacing.lg} 0;
  background: ${colors.background.secondary};

  legend {
    color: ${colors.text.primary};
    font-weight: 600;
    font-size: 14px;
    padding: 0 ${spacing.sm};
  }
`;

// Form containers
export const FormContainer = styled.div<{ maxWidth?: string }>`
  max-width: ${props => props.maxWidth || '600px'};
  margin: 0 auto;
  padding: ${spacing.xl};
  background: ${colors.background.secondary};
  border-radius: 16px;
  border: 1px solid ${colors.background.tertiary};
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);

  h3 {
    font-size: 24px;
    font-weight: 700;
    color: ${colors.text.primary};
    margin: 0 0 ${spacing.sm} 0;
    background: ${gradients.primary};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  p {
    font-size: 16px;
    color: ${colors.text.secondary};
    margin: 0 0 ${spacing.xl} 0;
    line-height: 1.6;
  }
`;

export const FormActions = styled.div<{ 
  align?: 'left' | 'center' | 'right';
  spacing?: 'sm' | 'md' | 'lg';
}>`
  display: flex;
  gap: ${props => {
    switch (props.spacing) {
      case 'sm': return spacing.sm;
      case 'lg': return spacing.lg;
      default: return spacing.md;
    }
  }};
  
  justify-content: ${props => {
    switch (props.align) {
      case 'center': return 'center';
      case 'right': return 'flex-end';
      default: return 'flex-start';
    }
  }};
  
  margin-top: ${spacing.lg};
`;
